export type BackendMenu = {
  id?: string | number;
  name?: string;
  title?: string;
  path?: string;
  route?: string;
  component?: string;
  icon?: string;
  redirect?: string;
  hidden?: boolean;
  meta?: {
    title?: string;
    icon?: string;
    hideMenu?: boolean;
    hidden?: boolean;
  };
  children?: BackendMenu[];
};

export type AppMenu = {
  key: string;
  title: string;
  path: string;
  component?: string;
  icon?: string;
  hide?: boolean;
  children?: AppMenu[];
};

export type AppRoute = {
  key: string;
  title: string;
  path: string;
  component?: string;
  breadcrumb: string[];
};

const normalizePath = (path?: string) => {
  const raw = (path || "").trim();
  if (!raw) {
    return "";
  }
  return raw.startsWith("/") ? raw : `/${raw}`;
};

const convertMenu = (
  menu: BackendMenu,
  index: number,
  parentKey = "menu"
): AppMenu => {
  const rawPath = normalizePath(menu.path || menu.route || "");
  const fallbackKey = `${parentKey}_${index}`;
  const key = rawPath.replace(/^\//, "") || fallbackKey;
  const title = menu.meta?.title || menu.title || menu.name || key;
  const children = Array.isArray(menu.children)
    ? menu.children
        .map((child, idx) => convertMenu(child, idx, key))
        .filter(Boolean)
    : undefined;
  const component = menu.component
    ? menu.component.trim().replace(/^\/+/, "")
    : undefined;
  return {
    key,
    title,
    path: rawPath || `/${fallbackKey}`,
    component,
    icon: menu.meta?.icon || menu.icon,
    hide: !!(
      menu.meta?.hideMenu ||
      menu.meta?.hidden ||
      menu.hidden ||
      rawPath === "/system/user/info" ||
      component === "system/user/info"
    ),
    children,
  };
};

export const normalizeMenuData = (menus: BackendMenu[] = []): AppMenu[] => {
  return menus.map((menu, index) => convertMenu(menu, index)).filter(Boolean);
};

export const flattenMenus = (
  menus: AppMenu[],
  parents: string[] = []
): AppRoute[] => {
  const result: AppRoute[] = [];
  menus.forEach((menu) => {
    const breadcrumb = menu.hide ? parents : [...parents, menu.title];
    if (menu.component) {
      result.push({
        key: menu.key,
        title: menu.title,
        path: menu.path,
        component: menu.component,
        breadcrumb,
      });
    }
    if (menu.children && menu.children.length) {
      result.push(...flattenMenus(menu.children, breadcrumb));
    }
  });
  return result;
};

export const buildParentMap = (
  menus: AppMenu[],
  parent?: string,
  map = new Map<string, string>()
) => {
  menus.forEach((menu) => {
    if (parent) {
      map.set(menu.path, parent);
    }
    if (menu.children && menu.children.length) {
      buildParentMap(menu.children, menu.path, map);
    }
  });
  return map;
};

export const getFirstMenuPath = (menus: AppMenu[]): string => {
  for (const menu of menus) {
    if (menu.component) {
      return menu.path;
    }
    if (menu.children && menu.children.length) {
      const childPath = getFirstMenuPath(menu.children);
      if (childPath) {
        return childPath;
      }
    }
  }
  return "";
};
